import {
  computed,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  Input,
  output,
  input,
  signal
} from '@angular/core';
import { NG_VALIDATORS } from '@angular/forms';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { distinctUntilChanged } from 'rxjs/operators';
import { DateService } from '../../../services/date/date.service';
import { DateTimeFormat } from '../../../services/date/date-formatter.service';

import { ValueAccessorProviderFactory } from '../../value-accessor/value-accessor-provider-factory';
import { DatePickerService } from '../date-picker/date-picker.service';

import type {
  OnInit,
  OnChanges,
  SimpleChanges,
  OutputEmitterRef,
  Signal,
  WritableSignal
} from '@angular/core';
import type { UntypedFormControl, Validator, ControlValueAccessor, ValidationErrors } from '@angular/forms';
import type { Subscription } from 'rxjs';

import type { DatePickerFormatType } from '../../../services/date/date-formatter.service';
import type { DateRange, RangeDateChange, DateRangeKeys } from '../date-range-selector.domain';
import type { DateValidator } from '../date-picker/date-validator.type';
import type { RangeSelectorCalendarHost } from './range-selector-calendar.contract';

@Directive({
  selector: '[tdWbDateRangeStartDatePicker], [tdWbDateRangeEndDatePicker]',
  providers: [
    ValueAccessorProviderFactory(RangeSelectorCalendarInputDirective),
    {
      provide: NG_VALIDATORS,
      useExisting: RangeSelectorCalendarInputDirective,
      multi: true
    }
  ],
  host: {
    '(change)': 'onChange($event.target)',
    '(input)': 'onInput($event.target)',
    '(focus)': 'onFocus()',
    '(blur)': 'onBlur()',
    '[attr.disabled]': 'disabledState() || null',
    '[attr.required]': 'requiredInput() || null',
    '[attr.maxlength]': 'getDateFormat().length',
    '[attr.placeholder]': 'computedPlaceholder()'
  }
})
export class RangeSelectorCalendarInputDirective implements ControlValueAccessor, OnInit, OnChanges, Validator {

  // ── DI ──────────────────────────────────────────────────────────────────
  private readonly elementRef = inject(ElementRef);
  private readonly datePickerService = inject(DatePickerService);
  private readonly destroyRef = inject(DestroyRef);

  // ── Outputs ──────────────────────────────────────────────────────────────
  readonly valueChange: OutputEmitterRef<RangeDateChange | null> = output<RangeDateChange | null>();
  readonly disabledChange: OutputEmitterRef<boolean> = output<boolean>();

  // ── Inputs ────────────────────────────────────────────────────────────────
  get value(): Date | null { return this._value; }

  /**
   * Control -> owner. Suppressed while applying an owner-initiated `writeValue()`,
   * which must never echo back (§4.2 criterion 4).
   */
  set value(value: Date | null) {
    const oldDate = this.value;
    this._value = value;
    if (!this.suppressOnChange) {
      this.onChangeCallback(value);
    }

    if (oldDate !== value) {
      this.valueChange.emit({ field: this.tdWbDateRangeKey, value });
    }
  }

  // TODO: Migrate tdWbDateRangeStartDatePicker / tdWbDateRangeEndDatePicker to signal inputs.
  //  These setters wire up `datepicker`, `tdWbDateRangeKey`, and `registerInput()` imperatively,
  //  which requires a more involved refactor before they can become plain `input()` signals.
  @Input()
  set tdWbDateRangeStartDatePicker(value: RangeSelectorCalendarHost) {
    if (value === undefined) {
      return;
    }
    this.datepicker = value;
    this.tdWbDateRangeKey = 'startDate';
    this.registerInput();
  }

  @Input()
  set tdWbDateRangeEndDatePicker(value: RangeSelectorCalendarHost) {
    if (value === undefined) {
      return;
    }
    this.datepicker = value;
    this.tdWbDateRangeKey = 'endDate';
    this.registerInput();
  }

  readonly disabledDates = input<DateValidator>(undefined);
  readonly noWeekends = input<boolean>(true);
  readonly format = input<DatePickerFormatType>(DateTimeFormat.YEAR_MONTH_DAY);
  readonly placeholder = input<string>(undefined);

  readonly disabledInput = input<boolean>(false, { alias: 'disabled' });
  readonly requiredInput = input<boolean>(false, { alias: 'required' });
  readonly idInput = input<string>(undefined, { alias: 'id' });

  /** Disabled state pushed imperatively by a form owner through `setDisabledState()`. */
  private readonly disabledByOwner: WritableSignal<boolean> = signal(false);

  /**
   * Effective disabled state.
   *
   * Providing NG_VALUE_ACCESSOR replaces Angular's DefaultValueAccessor, so nothing
   * else applies the disabled property on our behalf — the declarative `[disabled]`
   * binding and the imperative `setDisabledState()` must both be reconciled here.
   * See `wiki/signal-forms/core-signal-forms-revised-dual-support-plan.md` §4.2.
   */
  readonly disabledState: Signal<boolean> = computed(
    (): boolean => this.disabledInput() || this.disabledByOwner()
  );

  get disabled(): boolean { return this.disabledState(); }
  get required(): boolean { return this.requiredInput(); }
  get id(): string { return this.idInput(); }

  readonly minDateInput = input<Date>(undefined, { alias: 'minDate' });
  readonly maxDateInput = input<Date>(undefined, { alias: 'maxDate' });
  /**
   * Weekend-adjusted date bounds. Written reactively via signal subscriptions whenever
   * the `minDateInput` / `maxDateInput` signals change. Read as plain properties
   * by both `validate()` and `RangeSelectorCalendarComponent.getMinDate/getMaxDate`.
   */
  minDate: Date;
  maxDate: Date;

  private readonly normalizedMinDate = computed((): Date | undefined => {
    const raw = this.minDateInput();
    if (raw !== undefined && this.datePickerService.isDate(raw)) {
      return DateService.adjustForWeekend(DateService.convertDate(raw), 1);
    }
    return raw;
  });

  private readonly normalizedMaxDate = computed((): Date | undefined => {
    const raw = this.maxDateInput();
    if (raw !== undefined && this.datePickerService.isDate(raw)) {
      return DateService.adjustForWeekend(DateService.convertDate(raw), -1);
    }
    return raw;
  });

  computedPlaceholder(): string {
    const format = this.datePickerService.getPlaceholderFormat(this.getDateFormat());
    return this.placeholder() ?? format.toLocaleUpperCase();
  }

  disabledDateValidators: Array<DateValidator> = [];

  // ── Private state ─────────────────────────────────────────────────────────
  _disabled: boolean;  // kept for backwards-compat reads in specs; mirrors disabledInput()
  _required: boolean;  // kept for backwards-compat reads in specs; mirrors requiredInput()
  private tdWbDateRangeKey: DateRangeKeys;
  private datepicker: RangeSelectorCalendarHost;
  private datepickerSubscription: Subscription;
  private _value: Date | null;
  private formatted: string;
  private isEmpty: boolean;
  private dateRange: DateRange;
  private onNgChange: () => void;
  /** True while applying an owner-initiated write, to suppress the onChange echo. */
  private suppressOnChange: boolean = false;

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (this.datepickerSubscription) {
        this.datepickerSubscription.unsubscribe();
      }
    });

    // Emit disabledChange whenever the effective disabled state changes.
    toObservable(this.disabledState)
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((newValue: boolean) => {
        this._disabled = newValue;
        this.disabledChange.emit(newValue);
      });

    toObservable(this.normalizedMinDate)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((minDate: Date | undefined) => {
        this.minDate = minDate;
        this.notifyValidatorChange();
      });

    toObservable(this.normalizedMaxDate)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((maxDate: Date | undefined) => {
        this.maxDate = maxDate;
        this.notifyValidatorChange();
      });
  }

  onChange(target: EventTarget | null): void {
    const text = (target as HTMLInputElement)?.value;
    this.isEmpty = text.length === 0;
    this.setValue(this.parseStringToDate(text), false);
  }

  onInput(target: EventTarget | null): void {
    const text = (target as HTMLInputElement)?.value;
    this.isEmpty = text.length === 0;
    this.parser(text);
  }

  onFocus(): void {
    this.datepicker.setActiveCalendar(this.tdWbDateRangeKey);
  }

  /** Marks the control touched when the user leaves the field. */
  onBlur(): void {
    this.onTouchedCallback();
  }

  ngOnInit(): void {
    this.setValidators();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // minDate and maxDate are signal inputs — they do NOT appear in SimpleChanges.
    // Weekend adjustment is handled via signal subscriptions above.
    // Only invoke the validator change callback for other @Input changes.
    if (changes !== undefined && this.onNgChange !== undefined) {
      this.onNgChange();
    }
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onNgChange = fn;
  }

  private notifyValidatorChange(): void {
    if (this.onNgChange !== undefined) {
      this.onNgChange();
    }
  }

  // Implemented as part of ControlValueAccessor.
  writeValue(value: Date): void {
    // Owner -> control. Must not echo through onChangeCallback (§4.2 criterion 4);
    // echoing `undefined` removes the property from a Signal Forms model (NG01902).
    this.suppressOnChange = true;
    try {
      this.isEmpty = !this.datePickerService.isDate(value);
      this.setValue(value);
    } finally {
      this.suppressOnChange = false;
    }
  }

  // Implemented as part of ControlValueAccessor.
  registerOnChange(fn: (value: unknown) => void): void {
    this.onChangeCallback = fn;
  }

  // Implemented as part of ControlValueAccessor.
  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  /**
   * Implemented as part of ControlValueAccessor.
   *
   * Required: providing NG_VALUE_ACCESSOR replaces Angular's DefaultValueAccessor,
   * so without this a `control.disable()` would leave the input fully editable.
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabledByOwner.set(isDisabled);
  }

  /**
   * Validator function for date picker
   *
   * @param formControl The date picker form control
   * @returns Returns validation result
   */
  validate(formControl: UntypedFormControl): ValidationErrors | null {
    if (this.isEmpty) {
      if (this.required) {
        return { required: true };
      }
      return null;
    }
    if (this.getParsed() === null) {
      return { parse: true };
    }
    const minDate = this.minDate;
    if (this.datePickerService.isDate(minDate) && this.datePickerService.validateMinDate(minDate)(formControl.value)) {
      return { min: true };
    }
    const maxDate = this.maxDate;
    if (this.datePickerService.isDate(maxDate) && this.datePickerService.validateMaxDate(maxDate)(formControl.value)) {
      return { max: true };
    }
    if (this.isDisabledDate(formControl.value) && !this.datePickerService.validateNoWeekends()(formControl.value)) {
      return { weekend: true };
    }
    if (this.isDisabledDate(formControl.value)) {
      return { disabledDate: true };
    }
    if (
      (this.dateRange !== undefined && this.dateRange !== null) &&
      this.datePickerService.isDate(this.dateRange.startDate) &&
      this.datePickerService.isDate(this.dateRange.endDate) &&
      this.dateRange.endDate < this.dateRange.startDate
    ) {
      return { range: true };
    }
    return null;
  }

  isDisabledDate(date: Date): boolean {
    return this.datePickerService.isDisabledDate(date, this.disabledDates(), this.disabledDateValidators);
  }

  getDateFormat(): DatePickerFormatType {
    return this.datePickerService.getDateFormat(this.format());
  }

  private onChangeCallback: (_: unknown) => void = (): void => {};
  private onTouchedCallback: () => void = (): void => {};

  private setValue(date: Date, setToDefault: boolean = true): void {
    if (date !== undefined && this.datePickerService.isDate(date)) {
      this.setDateValue(date);
    } else if (this.isEmpty) {
      this.setEmptyValue();
    } else {
      this.setToDefaultValue(setToDefault);
    }
  }

  private parseStringToDate(text: string): Date | undefined {
    let result: Date;
    if (text.length !== 0) {
      const valid = this.datePickerService.validateDatePattern(this.format(), text);
      if (valid) {
        const date = this.datePickerService.parseDate(text, this.format());
        if (date !== null) {
          result = date;
        }
      }
    }
    return result;
  }

  private setValidators(): void {
    if (this.noWeekends()) {
      this.registerDisabledDateValidator(this.datePickerService.validateNoWeekends());
    }
  }

  private registerDisabledDateValidator(disabledDateValidator: DateValidator): void {
    this.disabledDateValidators.push(disabledDateValidator);
  }

  private getParsed(): Date | null {
    return DateService.isDate(this.value) ? this.value : null;
  }

  private registerInput(): void {
    this.datepicker.registerInput(this, this.tdWbDateRangeKey);
    this.datepickerSubscription = this.datepicker.selectedChanged
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selected: DateRange): void => {
        this.dateRange = selected;
        this.syncFromCalendar(selected[this.tdWbDateRangeKey]);
      });
  }

  /**
   * Applies a value reported by the sibling `RangeSelectorCalendarComponent`'s own
   * `selectedChanged` stream — e.g. a day-picker click, or the start/end range-order
   * normalization correcting the *other* field (WMPTRADE-38050).
   *
   * This is deliberately NOT `writeValue()`: `writeValue()` is the Angular Forms "owner push"
   * contract and must never echo through `onChangeCallback` (see criterion 4 above). But the
   * calendar invoking `selectedChanged` is not an owner pushing a stale value down — it is
   * reporting a genuine new value. Suppressing the echo here silently stranded corrected
   * dates inside the calendar: the displayed text updated, but the real owner (e.g. the
   * `[(ngModel)]`-bound `startDate`/`endDate` on `DateRangePickerComponent`) never observed
   * the change and went on to submit a stale/inverted custom range.
   */
  private syncFromCalendar(value: Date | null | undefined): void {
    this.isEmpty = !this.datePickerService.isDate(value);
    this.setValue(value ?? null);
  }

  private parser(text: string): void {
    const date = this.parseStringToDate(text);
    if (date !== undefined) {
      this.setDateValue(date);
    } else if (this.isEmpty) {
      this.setEmptyValue();
    }
  }

  private setDateValue(date: Date): void {
    this.value = date;
    this.formatted = this.datePickerService.formatDate(this.value, this.format());
    this.elementRef.nativeElement.value = this.formatted;
  }

  private setEmptyValue(): void {
    // `null`, not `undefined` — see §4.2 criterion 5.
    this.value = null;
    this.formatted = '';
    this.elementRef.nativeElement.value = this.formatted;
  }

  private setToDefaultValue(setToDefault: boolean = true): void {
    this.value = null;
    if (setToDefault) {
      this.formatted = '';
    }
    this.elementRef.nativeElement.value = this.formatted;
  }
}
